/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */

import { ClassificationLabel } from "./classification-label";
import { EntityLabel } from "./entity-label";
import { GroupingLabel } from "./grouping-label";
import { TabType } from "./processor-types";
import { showClearingButtonForQueue } from "./mapping";

export class DocumentInput {
  data: any;
  navigation: LayoutConfig;

  constructor(
    labels: EntityLabel[] | GroupingLabel[] | ClassificationLabel[],
    headerTitle: string,
    disablePrev: boolean = true,
    disableNext: boolean = true,
    type: TabType = "Entity",
    queueName?: string,
    schemaFirst: boolean = true,
  ) {
    let documentAnnotationData: any = {};

    documentAnnotationData.pages = [{
      "imageUrl": {
        "privateDoNotAccessOrElseWrappedUrl": ""
      },
      "tokens": [],
      "kvPairs": [],
      "entities": [],
      "tables": [],
      "textBlocks": [],
      "angle": 0
    }];

    // convert Document to DocumentAnnotationData which is different for splitting and entity extraction
    if (type === "DocSplitter" ) {
      // set groupingLabels
      documentAnnotationData.entityLabels = [];
      documentAnnotationData.groupingLabels = labels;
      // Explicitly disable search for DocSplitter
      documentAnnotationData.showSearchButton = false;
      // Disable rotate functionality for DocSplitter as it's not needed for splitting
      documentAnnotationData.allowRotate = false;
      // setup inital zoom for Splitter
      documentAnnotationData.zoomInfo = {
        // set center of zoom (x: 0, y:0 is left top corner)
        zoomCenter: { x: 0, y: 0 },
        // zoom level is a multiplier to the minimal zoom
        zoomLevel: 8,
      };
    } else if (type === "DAI_COMPLEX_CLASSIFICATION") {
      documentAnnotationData.entityLabels = [];
      documentAnnotationData.classificationLabels = labels;
      // Explicitly disable search for Classification
      documentAnnotationData.showSearchButton = false;
      // Enable rotate functionality for Classification
      documentAnnotationData.allowRotate = true;
      // setup inital zoom for Classification
      documentAnnotationData.zoomInfo = {
        // set center of zoom (x: 0, y:0 is left top corner)
        zoomCenter: { x: 0, y: 0 },
        // zoom level is a multiplier to the minimal zoom
        zoomLevel: 1.75,
      };
    } else {
      documentAnnotationData.entityLabels = labels;
      documentAnnotationData.editConfig = {
        // enable support for nested entities
        enableNestedEntities: true,
        // enable schema first labeling, e.g. show all values from the entityLabels even if they are not set in the document
        schemaFirst: schemaFirst,
      };
      // Enable rotate functionality for Entity extraction
      documentAnnotationData.allowRotate = true;
      // setup inital zoom for Extractor
      documentAnnotationData.zoomInfo = {
        // set center of zoom (x: 0, y:0 is left top corner)
        zoomCenter: { x: 0, y: 0 },
        // zoom level is a multiplier to the minimal zoom
        zoomLevel: 2,
      };
    }
    // Whether to show the escalate button.
    documentAnnotationData.showEscalateButton = false;
    // Whether to show the search button - only for Entity extraction (data extraction)
    // Explicitly set to false for DocSplitter and Classification
    if (type === "DocSplitter" || type === "DAI_COMPLEX_CLASSIFICATION") {
      documentAnnotationData.showSearchButton = false;
    } else if (type === "Entity") {
      documentAnnotationData.showSearchButton = true;
    } else {
      documentAnnotationData.showSearchButton = false;
    }
    // Display name of the document.
    documentAnnotationData.documentName = headerTitle;

    this.data = documentAnnotationData;

    // setup navigation layout
    this.navigation = new LayoutConfig(
      headerTitle,
      disablePrev,
      disableNext,
      type,
      queueName
    );
  }
}

// the LayoutConfig class sets up the look of the UI - most customizations can be done here
export class LayoutConfig {
  appMode = "HumanLabeling";
  // Controls whether to show/hide action button.
  isEditable = true;
  // Whether there was a local change, according to if there was/was not, the action button will be enabled/disabled. Required when isEditable is true.
  dataChanged = false;
  // Title for side panel header.
  headerTitle: string;
  // Controls whether to always enable primary button.
  enablePrimaryButton = true;
  // Button text for save button. Default value is 'Save'.
  primaryButtonText = "Speichern";
  // Optional text for secondary button. If not given, the secondary button will be invisible.
  secondaryButtonText?: string = "Clearing";
  // Disable previous button
  disablePrev: boolean;
  // Disable next button - will be overwritten by the constructor
  disableNext: boolean;
  // Disable the confirm dialog for unsaved warning
  disableConfirmDialog = false;
  // Controls whether to show/hide header - will be overwritten by the constructor
  hideHeader = false;
  // Controls whether to show/hide back button.
  hideBackButton = false;
  // set the current tab type - will be overwritten by the constructor
  tabGroupConfig = {
    currentTabType: "Entity",
  };

  constructor(
    headerTitle: string,
    disablePrev: boolean = true,
    disableNext: boolean = true,
    type: TabType,
    queueName?: string
  ) {
    this.headerTitle = headerTitle;
    this.disablePrev = disablePrev;
    this.disableNext = disableNext;
    this.tabGroupConfig.currentTabType = type;
    if (queueName) {
      if (!showClearingButtonForQueue(queueName)) {
        this.secondaryButtonText = undefined;
      }
    }
  }
}
